import React, { useState, useEffect } from 'react';
import {
  createBillingPortalSession,
  createCheckoutSession,
} from '../api/stripe';
import { getUserById } from '../api/user';
import { UserSubscriptionStatus } from '../types/user.types';
import {
  CreditCardIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface UserData {
  subscriptionStatus: UserSubscriptionStatus;
  stripeCustomerId?: string;
  subscription?: {
    trialStartDate?: string;
    trialEndDate?: string;
    currentPeriodEnd: string;
    status: string;
  };
}

const BillingInfo: React.FC = () => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userId = Number(localStorage.getItem('userId'));

  useEffect(() => {
    fetchUserData();
  }, [userId]);

  const calculateTrialDaysRemaining = (): number => {
    if (userData?.subscription?.trialEndDate) {
      const endDate = new Date(userData.subscription.trialEndDate);
      const now = new Date();
      const diffTime = endDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    }
    // Fallback: assume 7-day trial from user creation
    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + 7); // Default fallback
    const diffTime = trialEndDate.getTime() - Date.now();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const user = await getUserById(userId);
      setUserData({
        subscriptionStatus: user.subscriptionStatus,
        stripeCustomerId: user.stripeCustomerId,
        subscription: user.subscription
          ? {
              trialStartDate: user.subscription.trialStartDate,
              trialEndDate: user.subscription.trialEndDate,
              currentPeriodEnd: user.subscription.currentPeriodEnd,
              status: user.subscription.status,
            }
          : undefined,
      });
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load billing information');
    } finally {
      setIsLoading(false);
    }
  };

  // const handleManageBilling = async () => {
  //   if (!userData?.stripeCustomerId) {
  //     setError('No billing information found');
  //     return;
  //   }

  //   try {
  //     setIsRedirecting(true);
  //     const session = await createBillingPortalSession();
  //     if (session.url) {
  //       window.location.href = session.url;
  //     }
  //   } catch (err: any) {
  //     console.error('Error opening billing portal:', err);
  //     setError(err.message || 'Failed to open billing portal');
  //     setIsRedirecting(false);
  //   }
  // };

  const handleUpgrade = async () => {
    try {
      setIsRedirecting(true);
      const priceId = import.meta.env.VITE_STRIPE_PRICE_ID;
      if (!priceId) {
        throw new Error('Price ID not configured');
      }

      const session = await createCheckoutSession(priceId);
      if (session.url) {
        window.location.href = session.url;
      }
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError(err.message || 'Failed to start checkout process');
      setIsRedirecting(false);
    }
  };

  const getStatusDisplay = () => {
    if (!userData) return null;

    switch (userData.subscriptionStatus) {
      case UserSubscriptionStatus.TRIAL:
        const daysRemaining = calculateTrialDaysRemaining();
        const isExpiringSoon = daysRemaining <= 3;

        return {
          icon: isExpiringSoon ? ExclamationTriangleIcon : ClockIcon,
          color: isExpiringSoon ? 'text-orange' : 'text-violet',
          bgColor: isExpiringSoon
            ? 'bg-orange bg-opacity-10'
            : 'bg-violet bg-opacity-10',
          text: `Free Trial - ${daysRemaining} ${
            daysRemaining === 1 ? 'day' : 'days'
          } remaining`,
          description: isExpiringSoon
            ? 'Your trial is expiring soon! Upgrade to continue using all features.'
            : 'Enjoying your free trial. Upgrade anytime for continued access.',
        };

      case UserSubscriptionStatus.SUBSCRIPTION_ACTIVE:
        const nextBillingDate = userData.subscription?.currentPeriodEnd
          ? new Date(
              userData.subscription.currentPeriodEnd
            ).toLocaleDateString()
          : 'Unknown';

        return {
          icon: CheckCircleIcon,
          color: 'text-green-400',
          bgColor: 'bg-green-400 bg-opacity-10',
          text: 'Active Subscription',
          description: `Your subscription is active and billing is up to date. Next billing: ${nextBillingDate}.`,
        };

      case UserSubscriptionStatus.TRIAL_EXPIRED:
        return {
          icon: ExclamationTriangleIcon,
          color: 'text-red',
          bgColor: 'bg-red bg-opacity-10',
          text: 'Trial Expired',
          description:
            'Your trial has ended. Subscribe to continue using the service.',
        };

      default:
        return {
          icon: ExclamationTriangleIcon,
          color: 'text-grey-400',
          bgColor: 'bg-grey bg-opacity-10',
          text: 'Unknown Status',
          description: 'Unable to determine subscription status.',
        };
    }
  };

  if (isLoading) {
    return (
      <div className="bg-grey rounded-lg border border-white border-opacity-20 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-white bg-opacity-20 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-white bg-opacity-20 rounded w-full mb-2"></div>
          <div className="h-4 bg-white bg-opacity-20 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay();

  return (
    <div className="bg-grey rounded-lg border border-white border-opacity-20 p-6">
      <h3 className="text-lg font-semibold text-white mb-4">
        Billing & Subscription
      </h3>

      {error && (
        <div className="bg-red bg-opacity-20 border border-red rounded-md p-4 mb-4">
          <p className="text-red text-sm">{error}</p>
        </div>
      )}

      {statusDisplay && (
        <div className="space-y-4">
          {/* Status Card */}
          <div className={`${statusDisplay.bgColor} rounded-lg p-4`}>
            <div className="flex items-start gap-3">
              <statusDisplay.icon
                className={`w-6 h-6 ${statusDisplay.color} flex-shrink-0 mt-0.5`}
              />
              <div>
                <h4 className={`font-medium ${statusDisplay.color} mb-1`}>
                  {statusDisplay.text}
                </h4>
                <p className="text-white text-opacity-80 text-sm">
                  {statusDisplay.description}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {userData?.subscriptionStatus ===
              UserSubscriptionStatus.SUBSCRIPTION_ACTIVE && (
              <a
                href="mailto:alan@superpower.social"
                className="flex items-center gap-2 px-4 py-2 bg-violet hover:bg-violet-700 hover:bg-opacity-80 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Manage Subscription
              </a>
              // <button
              //   onClick={handleManageBilling}
              //   disabled={isRedirecting}
              //   className="flex items-center gap-2 px-4 py-2 bg-violet hover:bg-violet-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              // >
              //   {isRedirecting ? (
              //     <>
              //       <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              //       <span>Opening...</span>
              //     </>
              //   ) : (
              //     <>
              //       <CreditCardIcon className="w-4 h-4" />
              //       <span>Manage Billing</span>
              //     </>
              //   )}
              // </button>
            )}

            {(userData?.subscriptionStatus === UserSubscriptionStatus.TRIAL ||
              userData?.subscriptionStatus ===
                UserSubscriptionStatus.TRIAL_EXPIRED) && (
              <button
                onClick={handleUpgrade}
                disabled={isRedirecting}
                className="flex items-center gap-2 px-4 py-2 bg-violet hover:bg-violet-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRedirecting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Loading...</span>
                  </>
                ) : (
                  <>
                    <CreditCardIcon className="w-4 h-4" />
                    <span>Upgrade Now</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingInfo;
